/**
 * Guide registry — the single place guides are defined.
 *
 * A guide is a list of steps the overlay walks the user through. Pages take
 * part only by adding `data-guide-target="<name>"` to the element a step
 * should highlight; they carry no guide logic of their own.
 *
 * Shape:
 *   {
 *     id: 'create_team',                   // stable, matches the catalogue
 *     nameKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NAME', // shown on the card
 *     doneKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DONE', // the result line on
 *                                          // the "done" card shown when the
 *                                          // guide finishes
 *     descriptionKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DESCRIPTION', // the
 *                                          // line on the onboarding list
 *     tier: 2,                             // catalogue tier, 1 to 4; tier 2
 *                                          // guides are optional extras
 *     finishRoutes: ['settings_teams_finish'], // optional: arriving on one of
 *                                          // these ends the guide as finished
 *     steps: [
 *       {
 *         id: 'new-button',                // unique within the guide
 *         routes: ['settings_teams_list'], // route names the step is shown on
 *         routeParams: { sub_page: 'website' }, // optional: params the route
 *                                          // must also have (a route name
 *                                          // that serves many pages)
 *         target: 'teams-new-button',      // data-guide-target value (optional:
 *                                          // no target = centred card)
 *         titleKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NEW_BUTTON.TITLE',
 *         bodyKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NEW_BUTTON.BODY',
 *         params: { channel: 'Telegram' }, // optional: values for the copy
 *         placement: 'bottom',             // top | bottom | left | right, or
 *                                          // left-start | right-start (card's
 *                                          // top level with the target's)
 *         action: true,                    // optional: the user does this on
 *                                          // the page, so the card has no
 *                                          // Next (the guide moves on when
 *                                          // they do)
 *         disabledKey: '...DISABLED',      // optional: shown when the target
 *                                          // is a disabled button, saying
 *                                          // what unlocks it
 *         navigable: false,                // optional: don't push the user to
 *                                          // this step's page on Next/Back
 *                                          // (it needs an id from their own
 *                                          // action)
 *         advanceOnNextTarget: true,       // optional: move on once the NEXT
 *                                          // step's target appears on the same
 *                                          // page (a button that opens a modal)
 *         finishAfterClick: true,          // optional: finish the guide when
 *                                          // the user clicks this target and it
 *                                          // then goes away (a modal's submit
 *                                          // button after it saves)
 *       },
 *     ],
 *   }
 *
 * Button names inside copy are linked messages to the same i18n key the page
 * uses (`@:(SIDEBAR.NEW_INBOX)`), so the card can never name a button
 * differently from the page.
 *
 * While a guide runs, the user moving to a page that belongs to a later step
 * jumps the guide forward to that step, so "click New team" advances by
 * itself once they do.
 *
 * Completion stays manual: finishing or exiting a guide only clears the
 * overlay; the user marks the guide done from the onboarding list.
 */
export const PLACEMENTS = [
  'top',
  'bottom',
  'left',
  'right',
  // Beside the target with the card's top level with the target's top.
  'left-start',
  'right-start',
];

const keys = (guide, step) => ({
  titleKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.TITLE`,
  bodyKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.BODY`,
});

const withDisabled = (guide, step) => ({
  ...keys(guide, step),
  disabledKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.DISABLED`,
});

// Copy shared by every inbox guide (the steps before and after the channel's
// own form are the same for all channels).
const sharedInboxKeys = step => ({
  titleKey: `ONBOARDING_GUIDE.SHARED_INBOX.${step}.TITLE`,
  bodyKey: `ONBOARDING_GUIDE.SHARED_INBOX.${step}.BODY`,
});

const guideKeys = copy => ({
  nameKey: `ONBOARDING_GUIDE.GUIDES.${copy}.NAME`,
  doneKey: `ONBOARDING_GUIDE.GUIDES.${copy}.DONE`,
  descriptionKey: `ONBOARDING_GUIDE.GUIDES.${copy}.DESCRIPTION`,
});

/**
 * An "add a <channel> inbox" guide. Every channel shares the route flow
 * (inbox list, channel picker, the channel's form, platform confirmation,
 * agent picker); only the form's fields differ.
 *
 * @param {Object} args
 * @param {string} args.id guide id
 * @param {string} args.copy copy group in ONBOARDING_GUIDE.GUIDES
 * @param {string} args.channelKey the channel's key / sub_page param
 * @param {string} args.channelName shown in "Choose <channel>"
 * @param {Array<{id: string, target: string, copy: string}>} args.fields the
 *   form fields to point at, in order
 * @param {{target: string, disabled?: boolean}} args.submit the form's submit
 *   button; `disabled` when it stays disabled until the fields are filled
 */
const inboxGuide = ({ id, copy, channelKey, channelName, fields, submit }) => {
  const formStep = {
    routes: ['settings_inboxes_page_channel'],
    routeParams: { sub_page: channelKey },
  };
  return {
    id,
    ...guideKeys(copy),
    tier: 1,
    finishRoutes: ['settings_inbox_finish'],
    steps: [
      {
        id: 'new-button',
        routes: ['settings_inbox_list'],
        target: 'inbox-new-button',
        placement: 'bottom',
        action: true,
        ...sharedInboxKeys('NEW_BUTTON'),
      },
      {
        id: 'choose-channel',
        routes: ['settings_inbox_new'],
        target: `channel-${channelKey}`,
        placement: 'bottom',
        action: true,
        params: { channel: channelName },
        ...sharedInboxKeys('CHOOSE_CHANNEL'),
      },
      ...fields.map(field => ({
        ...formStep,
        id: field.id,
        target: field.target,
        placement: 'right',
        ...keys(copy, field.copy),
      })),
      {
        ...formStep,
        id: 'submit',
        target: submit.target,
        // Beside the button, level with its top: above is where the form's
        // fields are, and the user is still filling them in.
        placement: 'right-start',
        action: true,
        ...(submit.disabled
          ? withDisabled(copy, 'SUBMIT')
          : keys(copy, 'SUBMIT')),
      },
      {
        id: 'platform',
        routes: ['settings_inboxes_choose_platform'],
        navigable: false,
        target: 'platform-confirm',
        placement: 'top',
        action: true,
        ...sharedInboxKeys('PLATFORM'),
      },
      {
        id: 'agents',
        routes: ['settings_inboxes_add_agents'],
        navigable: false,
        target: 'inbox-agents-submit',
        placement: 'top',
        action: true,
        ...sharedInboxKeys('AGENTS'),
      },
    ],
  };
};

const CREATE_TEAM = {
  id: 'create_team',
  ...guideKeys('CREATE_TEAM'),
  tier: 2,
  finishRoutes: ['settings_teams_finish'],
  steps: [
    {
      id: 'new-button',
      routes: ['settings_teams_list'],
      target: 'teams-new-button',
      placement: 'bottom',
      action: true,
      ...keys('CREATE_TEAM', 'NEW_BUTTON'),
    },
    {
      id: 'name',
      routes: ['settings_teams_new'],
      target: 'team-form-name',
      placement: 'right',
      ...keys('CREATE_TEAM', 'NAME_FIELD'),
    },
    {
      id: 'submit',
      routes: ['settings_teams_new'],
      target: 'team-form-submit',
      placement: 'right-start',
      action: true,
      ...withDisabled('CREATE_TEAM', 'SUBMIT'),
    },
    {
      id: 'agents',
      routes: ['settings_teams_add_agents'],
      navigable: false,
      target: 'team-agent-selector',
      placement: 'top',
      action: true,
      ...keys('CREATE_TEAM', 'AGENTS'),
    },
  ],
};

const CREATE_WEBSITE_INBOX = inboxGuide({
  id: 'create_website_inbox',
  copy: 'CREATE_WEBSITE_INBOX',
  channelKey: 'website',
  channelName: 'Website',
  fields: [
    { id: 'name', target: 'website-name', copy: 'NAME_FIELD' },
    { id: 'domain', target: 'website-domain', copy: 'DOMAIN' },
  ],
  submit: { target: 'website-submit', disabled: true },
});

const CREATE_TELEGRAM_INBOX = inboxGuide({
  id: 'create_telegram_inbox',
  copy: 'CREATE_TELEGRAM_INBOX',
  channelKey: 'telegram',
  channelName: 'Telegram',
  fields: [{ id: 'token', target: 'telegram-token', copy: 'TOKEN' }],
  submit: { target: 'telegram-submit' },
});

const CREATE_LINE_INBOX = inboxGuide({
  id: 'create_line_inbox',
  copy: 'CREATE_LINE_INBOX',
  channelKey: 'line',
  channelName: 'LINE',
  fields: [
    { id: 'name', target: 'line-name', copy: 'NAME_FIELD' },
    { id: 'channel-id', target: 'line-channel-id', copy: 'CHANNEL_ID' },
    {
      id: 'channel-secret',
      target: 'line-channel-secret',
      copy: 'CHANNEL_SECRET',
    },
    {
      id: 'channel-token',
      target: 'line-channel-token',
      copy: 'CHANNEL_TOKEN',
    },
  ],
  submit: { target: 'line-submit' },
});

const CREATE_API_INBOX = inboxGuide({
  id: 'create_api_inbox',
  copy: 'CREATE_API_INBOX',
  channelKey: 'api',
  channelName: 'API',
  fields: [
    { id: 'name', target: 'api-name', copy: 'NAME_FIELD' },
    { id: 'webhook', target: 'api-webhook', copy: 'WEBHOOK' },
  ],
  submit: { target: 'api-submit' },
});

// The add-agent form is a modal on the agents page, so every step stays on
// one route: the guide moves on when the modal opens and finishes when its
// submit button is clicked and the modal goes away.
const INVITE_AGENT = {
  id: 'invite_agent',
  ...guideKeys('INVITE_AGENT'),
  tier: 1,
  steps: [
    {
      id: 'add-button',
      routes: ['agent_list'],
      target: 'agents-add-button',
      placement: 'bottom',
      action: true,
      advanceOnNextTarget: true,
      ...withDisabled('INVITE_AGENT', 'ADD_BUTTON'),
    },
    {
      id: 'name',
      routes: ['agent_list'],
      target: 'agent-form-name',
      placement: 'right',
      ...keys('INVITE_AGENT', 'NAME_FIELD'),
    },
    {
      id: 'role',
      routes: ['agent_list'],
      target: 'agent-form-role',
      placement: 'right',
      ...keys('INVITE_AGENT', 'ROLE'),
    },
    {
      id: 'email',
      routes: ['agent_list'],
      target: 'agent-form-email',
      placement: 'right',
      ...keys('INVITE_AGENT', 'EMAIL'),
    },
    {
      id: 'submit',
      routes: ['agent_list'],
      target: 'agent-form-submit',
      // Left of the button lands outside the modal, clear of the fields.
      placement: 'left',
      action: true,
      finishAfterClick: true,
      ...withDisabled('INVITE_AGENT', 'SUBMIT'),
    },
  ],
};

// Creating a knowledge base is its own set of pages (list, upload, processing),
// ending on the finishing page once the knowledge base exists.
const CREATE_KNOWLEDGE_BASE = {
  id: 'create_knowledge_base',
  ...guideKeys('CREATE_KNOWLEDGE_BASE'),
  tier: 1,
  finishRoutes: ['add_knowledge_finishing'],
  steps: [
    {
      id: 'create-button',
      routes: ['knowledge'],
      target: 'knowledge-create-button',
      placement: 'bottom',
      action: true,
      ...withDisabled('CREATE_KNOWLEDGE_BASE', 'CREATE_BUTTON'),
    },
    {
      id: 'name',
      routes: ['add_knowledge_upload'],
      target: 'knowledge-name',
      placement: 'right',
      ...keys('CREATE_KNOWLEDGE_BASE', 'NAME_FIELD'),
    },
    {
      id: 'file',
      routes: ['add_knowledge_upload'],
      target: 'knowledge-file',
      placement: 'right',
      ...keys('CREATE_KNOWLEDGE_BASE', 'FILE'),
    },
    {
      id: 'upload-next',
      routes: ['add_knowledge_upload'],
      target: 'knowledge-upload-next',
      placement: 'left-start',
      action: true,
      ...withDisabled('CREATE_KNOWLEDGE_BASE', 'UPLOAD_NEXT'),
    },
    {
      id: 'processing-next',
      routes: ['add_knowledge_processing'],
      navigable: false,
      target: 'knowledge-processing-next',
      placement: 'left-start',
      action: true,
      ...keys('CREATE_KNOWLEDGE_BASE', 'PROCESSING_NEXT'),
    },
  ],
};

// Configuring the bot happens in an inbox's settings (chatbot tab). There is
// no page the user lands on afterwards, so the last step is a plain step the
// user ends with Done once they have saved.
const CONFIGURE_BOT = {
  id: 'configure_bot',
  ...guideKeys('CONFIGURE_BOT'),
  tier: 1,
  steps: [
    {
      id: 'open-settings',
      routes: ['settings_inbox_list'],
      target: 'inbox-settings-button',
      placement: 'left',
      action: true,
      ...keys('CONFIGURE_BOT', 'OPEN_SETTINGS'),
    },
    {
      id: 'toggle',
      routes: ['settings_inbox_show'],
      navigable: false,
      target: 'chatbot-toggle',
      placement: 'bottom',
      ...keys('CONFIGURE_BOT', 'TOGGLE'),
    },
    {
      id: 'knowledge',
      routes: ['settings_inbox_show'],
      navigable: false,
      target: 'chatbot-knowledge',
      placement: 'right',
      ...keys('CONFIGURE_BOT', 'KNOWLEDGE'),
    },
    {
      id: 'instruction',
      routes: ['settings_inbox_show'],
      navigable: false,
      target: 'chatbot-instruction',
      placement: 'right',
      ...keys('CONFIGURE_BOT', 'INSTRUCTION'),
    },
    {
      id: 'save',
      routes: ['settings_inbox_show'],
      navigable: false,
      target: 'chatbot-save',
      placement: 'right-start',
      ...withDisabled('CONFIGURE_BOT', 'SAVE'),
    },
  ],
};

// Guides are added here as they are built (pilots first, then by tier).
export const GUIDES = {
  [CREATE_WEBSITE_INBOX.id]: CREATE_WEBSITE_INBOX,
  [CREATE_TELEGRAM_INBOX.id]: CREATE_TELEGRAM_INBOX,
  [CREATE_LINE_INBOX.id]: CREATE_LINE_INBOX,
  [CREATE_API_INBOX.id]: CREATE_API_INBOX,
  [INVITE_AGENT.id]: INVITE_AGENT,
  [CREATE_KNOWLEDGE_BASE.id]: CREATE_KNOWLEDGE_BASE,
  [CONFIGURE_BOT.id]: CONFIGURE_BOT,
  [CREATE_TEAM.id]: CREATE_TEAM,
};

export const getGuide = guideId => GUIDES[guideId] || null;

// Tier 2 of the catalogue is "optional onboarding features".
export const OPTIONAL_TIER = 2;
export const isOptionalGuide = guide => guide.tier === OPTIONAL_TIER;

/**
 * Returns a list of problems with a guide definition (empty when valid).
 * Used by the registry spec so a malformed guide fails CI rather than
 * silently rendering nothing.
 */
export const validateGuide = guide => {
  const problems = [];
  if (!guide || typeof guide.id !== 'string' || !guide.id) {
    return ['guide needs a string id'];
  }
  if (!Array.isArray(guide.steps) || guide.steps.length === 0) {
    return [`${guide.id}: needs at least one step`];
  }
  if (typeof guide.nameKey !== 'string' || !guide.nameKey) {
    problems.push(`${guide.id}: needs a nameKey`);
  }
  if (typeof guide.doneKey !== 'string' || !guide.doneKey) {
    problems.push(`${guide.id}: needs a doneKey`);
  }
  if (typeof guide.descriptionKey !== 'string' || !guide.descriptionKey) {
    problems.push(`${guide.id}: needs a descriptionKey`);
  }
  if (![1, 2, 3, 4].includes(guide.tier)) {
    problems.push(`${guide.id}: tier must be 1, 2, 3 or 4`);
  }
  if (
    guide.finishRoutes !== undefined &&
    (!Array.isArray(guide.finishRoutes) ||
      guide.finishRoutes.some(name => typeof name !== 'string'))
  ) {
    problems.push(`${guide.id}: finishRoutes must be an array of route names`);
  }
  const seen = new Set();
  guide.steps.forEach((step, index) => {
    const label = `${guide.id} step ${index + 1}`;
    if (!step.id) problems.push(`${label}: missing id`);
    if (seen.has(step.id)) problems.push(`${label}: duplicate id ${step.id}`);
    seen.add(step.id);
    if (!Array.isArray(step.routes) || step.routes.length === 0) {
      problems.push(`${label}: needs a non-empty routes array`);
    }
    if (!step.titleKey || !step.bodyKey) {
      problems.push(`${label}: needs titleKey and bodyKey`);
    }
    if (step.placement && !PLACEMENTS.includes(step.placement)) {
      problems.push(`${label}: unknown placement ${step.placement}`);
    }
    if (
      step.routeParams !== undefined &&
      (typeof step.routeParams !== 'object' || step.routeParams === null)
    ) {
      problems.push(`${label}: routeParams must be an object`);
    }
    if (
      step.params !== undefined &&
      (typeof step.params !== 'object' || step.params === null)
    ) {
      problems.push(`${label}: params must be an object`);
    }
    if (step.disabledKey !== undefined && !step.target) {
      problems.push(`${label}: disabledKey needs a target`);
    }
    if (step.action !== undefined && typeof step.action !== 'boolean') {
      problems.push(`${label}: action must be a boolean`);
    }
    if (step.advanceOnNextTarget) {
      const next = guide.steps[index + 1];
      if (!next || !next.target) {
        problems.push(
          `${label}: advanceOnNextTarget needs a next step with a target`
        );
      }
    }
    if (step.finishAfterClick && !step.target) {
      problems.push(`${label}: finishAfterClick needs a target`);
    }
  });

  // A guide has to be able to end: by reaching a finish route, by a finishing
  // click, or by the user pressing Done on a last step that has a Next.
  const last = guide.steps[guide.steps.length - 1];
  const canFinish =
    (guide.finishRoutes || []).length > 0 ||
    guide.steps.some(step => step.finishAfterClick) ||
    !last.action;
  if (!canFinish) problems.push(`${guide.id}: has no way to finish`);
  return problems;
};
